<template>
  <ul class="download-link__list" v-if="requestedFiles.length > 0">
    <li v-for="(file, index) in requestedFiles" :key="file.id">
      <span>{{ fileTypeLabel(file) }}</span>
      <button type="button" class="download-link__preview-button"
        :class="{ 'download-link__preview-button--open': previewedFileId === file.id }" :disabled="!isPreviewable(file)"
        @click="togglePreview(file.id)">
        <span class="download-link__preview-label">Preview</span>
      </button>
      <a :href="file.url" :download="fileName(file, index + 1)" @click="handleDownload($event, file, index)">
        Download
      </a>
      <div class="download-link__preview-thumbnail" v-if="isPreviewable(file) && previewedFileId === file.id">
        <img :src="file.url" alt="" />
      </div>
    </li>
  </ul>
  <div v-else class="download-link__request">
    <themed-button :disabled="isLoading" @click="requestDownload">
      {{ isLoading ? 'Requesting...' : 'Request Download Links' }}
    </themed-button>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  props: ['files'],

  data() {
    return {
      requestedFiles: [],
      previewedFileId: null,
      isLoading: false,
    };
  },

  methods: {
    ...mapActions(['setMessage']),

    requestDownload() {
      if (!this.files || this.files.length === 0) {
        return Promise.resolve();
      }
      this.isLoading = true;

      const requests = this.files.map((file) => {
        const downloadUrl = this.buildDownloadUrl(file);
        return this.axios.get(`uploads/${file}`)
          .then((response) => {
            const { upload } = response.data;
            this.requestedFiles.push({
              id: file,
              file_type: upload.file_type,
              mime_type: upload.mime_type,
              downloadUrl,
            });
            const fileIndex = this.requestedFiles.findIndex(foundFile => foundFile.id === file);
            return this.axios.get(`uploads/${file}/download`, { responseType: 'blob' })
              .then((downloadResponse) => {
                this.requestedFiles[fileIndex].url = URL.createObjectURL(downloadResponse.data);
                this.requestedFiles[fileIndex].blob = downloadResponse.data;
              });
          })
          .catch((error) => {
            const message = (error && error.response && error.response.data && error.response.data.message)
              || 'request failed';
            this.showToast(`Cannot request download link:\n${message}`, 'error');
          });
      });

      return Promise.allSettled(requests).finally(() => {
        this.isLoading = false;
      });
    },

    handleDownload(event, file, index) {
      const isCordova = !!window.cordova;
      const platformId = window.cordova && window.cordova.platformId;
      const isAndroid = isCordova && platformId === 'android';

      if (event && (window.navigator.msSaveBlob || isCordova)) {
        event.preventDefault();
      }

      if (!file || (!file.url && !file.downloadUrl)) {
        this.showToast('Cannot download file: download is not ready yet', 'error');
        return;
      }

      if (window.navigator.msSaveBlob) {
        this.showToast('Download started', 'success');
        window.navigator.msSaveBlob(file.blob, this.fileName(file, index + 1));
      } else if (isAndroid) {
        const execFn = (window.cordova && typeof window.cordova.exec === 'function' && window.cordova.exec)
          || (window.cordova && window.cordova.require && window.cordova.require('cordova/exec'));
        const downloadUrl = (file && file.downloadUrl) || (file && file.id ? this.buildDownloadUrl(file.id) : '');

        if (execFn && downloadUrl) {
          const fileName = this.fileName(file, index + 1);
          const headers = [];
          const sessionId = this.getSessionId();
          if (sessionId) {
            headers.push({ header: 'Authorization', value: sessionId });
          }
          const request = {
            uri: downloadUrl,
            title: fileName,
            description: 'Downloading file',
            mimeType: file.mime_type,
            visibleInDownloadsUi: true,
            notificationVisibility: 1,
            destinationInExternalPublicDir: {
              dirType: 'Download',
              subPath: fileName,
            },
            headers,
          };

          execFn(
            () => this.showToast('Download started', 'success'),
            () => this.showToast('Cannot download file: failed to start download', 'error'),
            'DownloadManagerPlugin',
            'enqueue',
            [request],
          );
          return;
        }

        this.showToast(
          execFn
            ? 'Cannot download file: download link is not ready yet'
            : 'Cannot download file: Android downloads are unavailable. Please update the app.',
          'error',
        );
      } else if (isCordova) {
        if (!file || !file.blob) {
          this.showToast('Cannot download file: download is not ready yet', 'error');
          return;
        }

        const handleError = () => {
          this.showToast('Cannot download file: failed to save to device', 'error');
        };

        window.resolveLocalFileSystemURL(window.cordova.file.cacheDirectory, (dir) => {
          dir.getFile(this.fileName(file, index + 1), { create: true, exclusive: false }, (fileEntry) => {
            fileEntry.createWriter((writer) => {
              writer.onwrite = (tempFile) => {
                window.resolveLocalFileSystemURL(tempFile.target.localURL, (entry) => {
                  if (cordova.plugins && cordova.plugins.fileOpener2) {
                    cordova.plugins.fileOpener2.open(entry.toURL(), file.mime_type);
                  }
                  this.showToast('Download opened', 'success');
                }, handleError);
              };
              writer.onerror = handleError;
              writer.write(file.blob);
            }, handleError);
          }, handleError);
        }, handleError);
      } else {
        this.showToast('Download started', 'success');
      }
    },

    fileName(file, number) {
      return `File ${number}.${file.file_type}`;
    },

    fileTypeLabel(file) {
      return file && file.file_type ? file.file_type.toUpperCase() : 'file';
    },

    isPreviewable(file) {
      return file && file.mime_type && file.mime_type.startsWith('image/');
    },

    togglePreview(fileId) {
      this.previewedFileId = this.previewedFileId === fileId ? null : fileId;
    },

    showToast(text, messageType) {
      this.setMessage({ text, messageType });
    },

    getSessionId() {
      return this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.sessionId;
    },

    getBaseUrl() {
      return (this.axios && this.axios.defaults && this.axios.defaults.baseURL)
        || (process.env.API_HOST ? process.env.API_HOST.replace(/"/g, '') : '');
    },

    buildDownloadUrl(fileId) {
      const baseUrl = this.getBaseUrl();
      return baseUrl ? `${baseUrl.replace(/\/$/, '')}/uploads/${fileId}/download` : '';
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/styles/variables';

/* Layout container for the list of files */
.download-link__list {
  display: grid;
  row-gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;

  span {
    white-space: nowrap;
  }

  /* Three columns: file type, preview, download */
  >li {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 8rem 5rem;
    align-items: center;
  }
}

/* Preview button + toggle arrow */
.download-link__preview-button {
  display: inline-flex;
  align-items: center;
  margin-left: 3rem;
  cursor: pointer;
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  text-decoration: none;
  font: inherit;

  &::after {
    content: ' >';
    margin-left: 0.3rem;
    text-decoration: none;
    transform: rotate(0deg);
    transition: transform 0.15s ease;
  }

  /* no hover on mobile */
  &:not(:disabled):hover {
    @media (hover: hover) and (pointer: fine) {
      .download-link__preview-label {
        text-decoration: underline;
      }
    }
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
    text-decoration: none;

    &::after {
      content: '';
    }
  }
}

.download-link__preview-label {
  text-decoration: none;
}
.download-link__preview-button--open .download-link__preview-label {
  text-decoration: underline;
}
.download-link__preview-button--open::after {
  transform: rotate(90deg);
}

/* Inline preview container below the row */
.download-link__preview-thumbnail {
  grid-column: 1 / -1;
  margin-top: 6px;
  padding: 6px;
  border: 1px solid $grey;
  background: #fff;

  img {
    display: block;
    max-width: 220px;
    max-height: 160px;
  }
}
</style>
